import BackendService from 'BackendService';
import { getAssetStorageAccessToken } from './BaseBackendService';

export interface FetchRequest {
  id: string;
  path: string;
  resolve: (content: string | null) => void;
  reject: (error: any) => void;
  timestamp: number;
}

export interface BulkFetchConfig {
  batchInterval: number; // milliseconds
  maxWaitTime: number; // milliseconds
  maxBatchSize?: number; // optional limit on batch size
  maxConcurrentRequests?: number; // limit concurrent requests to GCP
}

export class BulkFetchService {
  private fetchQueue: Map<string, FetchRequest> = new Map();
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private isProcessing = false;
  private config: BulkFetchConfig;
  private backendService: BackendService;
  private globalConfig: any = null;

  constructor(
    backendService: BackendService,
    config: BulkFetchConfig = {
      batchInterval: 1000, // 1 second
      maxWaitTime: 3000, // 3 seconds max wait
      maxBatchSize: 20, // reasonable batch size
      maxConcurrentRequests: 20, // limit concurrent GCP requests
    },
  ) {
    this.backendService = backendService;
    this.config = config;
    this.loadGlobalConfig();
  }

  private async loadGlobalConfig(): Promise<void> {
    try {
      this.globalConfig = await this.backendService.getGlobalConfiguration();
    } catch (error) {
      console.warn('Failed to load global configuration for BulkFetchService:', error);
    }
  }

  private getEffectiveConfig(): BulkFetchConfig {
    if (!this.globalConfig) {
      return this.config;
    }

    return {
      ...this.config,
      maxConcurrentRequests:
        this.globalConfig.concurrentFrontendStorageRequests || this.config.maxConcurrentRequests,
      maxBatchSize:
        this.globalConfig.concurrentFrontendStorageBatchSize || this.config.maxBatchSize,
    };
  }

  async bypassQueue(id: string, path: string): Promise<string | null> {
    let accessToken: string | null = null;

    try {
      const tokenData = await this.backendService.setupAssetStorageWithRetry(false);
      accessToken = getAssetStorageAccessToken(tokenData);
    } catch (error) {
      console.error('Error setting up asset storage:', error);
      throw error;
    }

    if (!accessToken) {
      throw new Error('No asset storage access token');
    }

    try {
      const content = await this.backendService.getAsset(path, accessToken, {
        url: false,
      });

      return content as string | null;
    } catch (error) {
      console.error('Error getting asset:', error);
      throw error;
    }
  }

  /**
   * Queue a fetch request that will be processed in bulk
   */
  queueFetch(id: string, path: string): Promise<string | null> {
    if (path.includes('contentManagement') || path.includes('finance-news')) {
      return this.bypassQueue(id, path);
    }

    return new Promise((resolve, reject) => {
      // If already queued, return the existing promise
      if (this.fetchQueue.has(id)) {
        const existing = this.fetchQueue.get(id)!;
        // Replace callbacks to support multiple callers
        const originalResolve = existing.resolve;
        const originalReject = existing.reject;

        existing.resolve = (content: string | null) => {
          originalResolve(content);
          resolve(content);
        };
        existing.reject = (error: any) => {
          originalReject(error);
          reject(error);
        };
        return;
      }

      const request: FetchRequest = {
        id,
        path,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.fetchQueue.set(id, request);

      // Check if any items have exceeded max wait time
      this.checkMaxWaitTime();

      // Schedule batch processing if not already scheduled
      this.scheduleBatchProcess();
    });
  }

  private scheduleBatchProcess(): void {
    if (this.batchTimer || this.isProcessing) {
      return;
    }

    const effectiveConfig = this.getEffectiveConfig();
    this.batchTimer = setTimeout(() => {
      this.processBatch();
    }, effectiveConfig.batchInterval);
  }

  private checkMaxWaitTime(): void {
    const now = Date.now();
    const expiredRequests: FetchRequest[] = [];

    // Convert to array to avoid iterator issues
    const allRequests = Array.from(this.fetchQueue.values());
    const effectiveConfig = this.getEffectiveConfig();
    for (const request of allRequests) {
      if (now - request.timestamp >= effectiveConfig.maxWaitTime) {
        expiredRequests.push(request);
      }
    }

    if (expiredRequests.length > 0) {
      // Force immediate processing for expired requests
      this.processBatch(expiredRequests);
    }
  }

  /**
   * Process requests with concurrency control to avoid overwhelming GCP
   */
  private async processWithConcurrencyLimit(requests: FetchRequest[]): Promise<void> {
    const effectiveConfig = this.getEffectiveConfig();
    const concurrencyLimit = effectiveConfig.maxConcurrentRequests || 8;

    for (let i = 0; i < requests.length; i += concurrencyLimit) {
      const batch = requests.slice(i, i + concurrencyLimit);
      const batchPromises = batch.map(async (request) => {
        try {
          const tokenData = await this.backendService.setupAssetStorageWithRetry(false);
          const accessToken = getAssetStorageAccessToken(tokenData);
          if (!accessToken) {
            throw new Error('No asset storage access token');
          }
          const content = await this.backendService.getAsset(request.path, accessToken, {
            url: false,
          });

          request.resolve(content as string);
          this.fetchQueue.delete(request.id);
        } catch (error) {
          // For 404 errors, return null instead of rejecting
          if (error === 'Not Found') {
            request.resolve(null);
          } else {
            request.reject(error);
          }
          this.fetchQueue.delete(request.id);
        }
      });

      // Wait for this batch to complete before starting the next
      await Promise.allSettled(batchPromises);
    }
  }

  private async processBatch(specificRequests?: FetchRequest[]): Promise<void> {
    if (this.isProcessing && !specificRequests) {
      return;
    }

    this.isProcessing = true;

    try {
      // Clear the timer since we're processing now
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
        this.batchTimer = null;
      }

      // Determine which requests to process
      const requestsToProcess = specificRequests || Array.from(this.fetchQueue.values());

      if (requestsToProcess.length === 0) {
        return;
      }

      // Limit batch size if configured
      const effectiveConfig = this.getEffectiveConfig();
      const batchRequests = effectiveConfig.maxBatchSize
        ? requestsToProcess.slice(0, effectiveConfig.maxBatchSize)
        : requestsToProcess;

      // Process requests with concurrency control instead of unlimited parallel
      await this.processWithConcurrencyLimit(batchRequests);

      // If there are more requests in the queue, schedule another batch
      if (this.fetchQueue.size > 0) {
        this.scheduleBatchProcess();
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Clear all pending requests (useful for testing or cleanup)
   */
  clearQueue(): void {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    // Reject all pending requests
    const allRequests = Array.from(this.fetchQueue.values());
    for (const request of allRequests) {
      request.reject(new Error('Queue cleared'));
    }

    this.fetchQueue.clear();
  }

  /**
   * Get current queue size (useful for monitoring)
   */
  getQueueSize(): number {
    return this.fetchQueue.size;
  }
}

// Create singleton instances for different contexts
let nonAdminBulkFetchService: BulkFetchService | null = null;
let adminBulkFetchService: BulkFetchService | null = null;

export function getNonAdminBulkFetchService(): BulkFetchService {
  if (!nonAdminBulkFetchService) {
    // Lazy import to avoid circular dependencies;
    nonAdminBulkFetchService = new BulkFetchService(new BackendService());
  }
  return nonAdminBulkFetchService;
}

export function getAdminBulkFetchService(): BulkFetchService {
  if (!adminBulkFetchService) {
    // Lazy import to avoid circular dependencies

    adminBulkFetchService = new BulkFetchService(new BackendService());
  }
  return adminBulkFetchService;
}

// For testing, allow resetting the singletons
export function resetBulkFetchServices(): void {
  if (nonAdminBulkFetchService) {
    nonAdminBulkFetchService.clearQueue();
    nonAdminBulkFetchService = null;
  }
  if (adminBulkFetchService) {
    adminBulkFetchService.clearQueue();
    adminBulkFetchService = null;
  }
}
